import { FC } from 'react';
import { Stack, Paper, Button } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import {
  BirthMeasurementsData,
  ListContentAction,
} from '../utils/medical-history.types';
import BirthMeasurementsListContent from './BirthMeasurementsListContent';
import { notifications } from '@mantine/notifications';

export interface BirthMeasurementsListProps {
  data: BirthMeasurementsData;
  onAdd: () => void;
  onDelete: () => Promise<void>;
  readonly?: boolean;
}

export const BirthMeasurementsList: FC<BirthMeasurementsListProps> = ({
  data,
  onAdd,
  onDelete,
  readonly = false,
}) => {
  const { t } = useTranslation(['patient-pages']);

  const hasWeight = !!data.birthWeight?.valueQuantity?.value;
  const hasLength = !!data.birthLength?.valueQuantity?.value;
  const hasHeadCircumference =
    !!data.birthHeadCircumference?.valueQuantity?.value;
  const hasData = hasWeight || hasLength || hasHeadCircumference;

  const handleAction = async (action: ListContentAction) => {
    if (action === 'edit') {
      onAdd();
      return;
    }

    if (action === 'remove') {
      try {
        await onDelete();
        notifications.show({
          color: 'green',
          title: t('medicalHistory.deleteSuccess', {
            ns: 'patient-pages',
            defaultValue: 'Deleted',
          }),
          message: t('medicalHistory.birthMeasurements.deleteSuccessMessage', {
            ns: 'patient-pages',
            defaultValue: 'Birth measurements deleted successfully',
          }),
        });
      } catch (error) {
        console.error('Error deleting birth measurements:', error);
        notifications.show({
          color: 'red',
          title: t('medicalHistory.deleteError', {
            ns: 'patient-pages',
            defaultValue: 'Error',
          }),
          message: t('medicalHistory.birthMeasurements.deleteErrorMessage', {
            ns: 'patient-pages',
            defaultValue: 'Failed to delete birth measurements',
          }),
        });
      }
    }
  };

  return (
    <Stack gap="xs">
      <Paper
        withBorder
        radius="md"
        p="sm"
        bg={!hasData ? 'gray.0' : 'transparent'}
        pb={hasData ? 'sm' : 'xl'}
      >
        <BirthMeasurementsListContent
          data={data}
          onAction={handleAction}
          readonly={readonly}
        />
      </Paper>

      {/* Edit button (below the item) */}
      {!readonly && !hasData && (
        <Button
          leftSection={<IconPlus size={16} />}
          variant="light"
          size="sm"
          onClick={onAdd}
        >
          {t('medicalHistory.birthMeasurements.add', {
            defaultValue: 'Add Birth Measurements',
          })}
        </Button>
      )}
    </Stack>
  );
};

export default BirthMeasurementsList;
